import { BaseBlok, BlokToComponentProps, blokToComponent, StringService, useTranslation } from '@repo/roche-xx-shared';
import {
  RocheUIResultVariant,
  RocheUISearch,
  RocheUISearchTabResult,
  RocheUISearchTrigger,
} from '@rocheglobal/roche-ui';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchTrackingType } from '../tracking';
import { useSearchAriaLabels } from '../use-search-aria-labels';
import { MainSearchItem } from './list-items';
import { RocheSearchResultItem, SearchCategory } from './queries';
import { useSearch } from './use-search';

const triggerToTracking = (searchTrigger: RocheUISearchTrigger): SearchTrackingType => {
  return searchTrigger === RocheUISearchTrigger.POPULAR_SEARCHES
    ? SearchTrackingType.SEARCH_SUGGESTION
    : SearchTrackingType.MAIN_SEARCH;
};

const categories = Object.values(SearchCategory);

export type MainSearchProps = BlokToComponentProps<{
  total_results_found_text: string;
  slotted_quick_links: BaseBlok[];
  slotted_popular_search: { label: string }[];
}>;
export const MainSearch: FC<MainSearchProps> = ({ blok, getComponent }): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams();
  // tracking: keep "track" of how the search was triggered, can be by using the input,
  // but also by clicking on a popular search
  const [searchTrigger, setSearchTrigger] = useState<RocheUISearchTrigger>(RocheUISearchTrigger.INPUT);

  // insecure  inputs, as the user can manipulate the URL - we just trust it
  const searchQuery = searchParams.get('query') || '';
  const currentPage = Number(searchParams.get('page')) || 1;
  const currentTab = (searchParams.get('tab') || 'pages') as SearchCategory;

  // Refs for scroll behavior
  const resultsRef = useRef<HTMLDivElement>(null);
  const previousSearchQueryRef = useRef(searchQuery);
  const previousPageRef = useRef(currentPage);
  const previousTabRef = useRef(currentTab);

  // translations
  const searchTitle = StringService.capitalize(useTranslation('search'));
  const searchPlaceholder = StringService.capitalize(useTranslation('enter your search term'));
  const popularSearchesTitle = StringService.capitalize(useTranslation('popular searches:'));

  const totalResultsFoundText =
    blok.total_results_found_text ?? StringService.capitalize(useTranslation('total results found'));
  const quickLinksTitle = StringService.capitalize(useTranslation('quick links'));
  const loadingText = StringService.capitalize(useTranslation('loading'));
  const noResultsMessage = useTranslation('please try with another term.');
  const noResultsTitle = useTranslation('sorry, no results found.');

  const pagesTitle = StringService.capitalize(useTranslation('pages'));
  const storiesTitle = StringService.capitalize(useTranslation('stories'));
  const documentsTitle = StringService.capitalize(useTranslation('documents'));

  // aria labels
  const ariaLabels = useSearchAriaLabels();

  // Map over categories to create query functions for each category (pages, stories, documents)
  const categoryQueryFns = categories.map((category) => {
    const page = currentTab === category ? currentPage : 1;
    const queryFn = useSearch({
      searchQuery,
      page,
      categoryFilter: category,
      searchTrigger: triggerToTracking(searchTrigger),
      shouldTrack: currentTab === category,
    });
    return { category, queryFn };
  });

  // Map category to translated title
  const getCategoryTitle = useCallback(
    (category: SearchCategory): string => {
      switch (category) {
        case SearchCategory.PAGES:
          return pagesTitle;
        case SearchCategory.STORIES:
          return storiesTitle;
        case SearchCategory.DOCUMENTS:
          return documentsTitle;
        default:
          return category;
      }
    },
    [pagesTitle, storiesTitle, documentsTitle],
  );

  // Map and memoize over categories to create search tabs
  const searchResults: RocheUISearchTabResult<SearchCategory, RocheSearchResultItem>[] = useMemo(
    () =>
      categoryQueryFns.map(({ category, queryFn }) => ({
        tab: category,
        variant: category as RocheUIResultVariant,
        title: getCategoryTitle(category),
        items: queryFn.data?.Items || [],
        totalItems: queryFn.data?.Total || 0,
      })),
    [categoryQueryFns, getCategoryTitle],
  );

  // Determine if any query is loading at the moment
  const isLoading = categoryQueryFns.some(({ queryFn }) => queryFn.isLoading);

  // Check if queries have been fetched at least once (to distinguish between initial loading and no results)
  const hasQueriesBeenFetched = categoryQueryFns.some(({ queryFn }) => queryFn.isFetched);

  // Memoize quick link elements
  const quickLinkElements = useMemo(
    () =>
      blok.slotted_quick_links.map((teaser, index) => {
        return blokToComponent(
          {
            slot: 'quick-links',
            blok: teaser,
            getComponent,
            keyForChildren: `quick-link-${index}`,
            parentComponent: 'roche-search',
          },
          'search-quick-links',
        );
      }),
    [blok.slotted_quick_links, getComponent],
  );

  const currentPageResults = useMemo(
    () => searchResults.find((result) => result.tab === currentTab),
    [searchResults, currentTab],
  );

  // scroll behavior
  useEffect(() => {
    const hasResults = searchResults.some((tab) => tab.items.length > 0);
    const hasSearchQuery = searchQuery.trim().length > 0;

    // Don't scroll if no results or no search query
    if (!hasResults || !hasSearchQuery) {
      return;
    }

    const searchQueryChanged = previousSearchQueryRef.current !== searchQuery;
    const pageChanged = previousPageRef.current !== currentPage;
    const tabChanged = previousTabRef.current !== currentTab;

    if (resultsRef.current && !tabChanged && (searchQueryChanged || pageChanged)) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    previousSearchQueryRef.current = searchQuery;
    previousPageRef.current = currentPage;
    previousTabRef.current = currentTab;
  }, [searchQuery, currentPage, currentTab, searchResults, isLoading]);

  // Memoize itemRenderer
  const itemRenderer = useCallback(
    (item: RocheSearchResultItem, tab: SearchCategory, index: number, page: number) => (
      <MainSearchItem
        key={`search-result-${tab}-${index}-${page}`}
        item={item}
        tab={tab}
        trackingData={{
          term: searchQuery,
          results: currentPageResults?.totalItems || 0,
          resultPosition: index,
          filter: {
            pagination: currentPage,
            filter: currentTab,
          },
          searchComponent: triggerToTracking(searchTrigger),
        }}
      />
    ),
    [searchQuery, currentPageResults, currentPage, currentTab, searchTrigger],
  );

  // Memoize popularSearches and quickLinks
  const support = useMemo(
    () => ({
      popularSearches: blok.slotted_popular_search.map((popularSearch) => ({ label: popularSearch.label })),
      quick_links: quickLinkElements,
      labels: {
        popularSearchesTitle,
        quickLinksTitle,
      },
    }),
    [blok.slotted_popular_search, quickLinkElements, popularSearchesTitle, quickLinksTitle],
  );

  // Memoize searchState
  const searchState = useMemo(
    () => ({
      query: searchQuery,
      page: currentPage,
      activeTab: currentTab,
      trigger: searchTrigger,
    }),
    [searchQuery, currentPage, currentTab, searchTrigger],
  );

  // Memoize labels
  const labels = useMemo(
    () => ({
      searchTitle,
      searchPlaceholder,
      totalResultsFoundText,
      loadingText,
      noResultsTitle,
      noResultsMessage,
    }),
    [searchTitle, searchPlaceholder, totalResultsFoundText, loadingText, noResultsTitle, noResultsMessage],
  );

  // Memoize onSearchParamsChange
  const handleSearchParamsChange = useCallback(
    (params: { query: string; page: number; activeTab: SearchCategory; trigger?: RocheUISearchTrigger }) => {
      if (params.trigger !== undefined) {
        setSearchTrigger(params.trigger);
      }
      setSearchParams({
        query: params.query,
        page: params.page.toString(),
        tab: params.activeTab,
      });
    },
    [setSearchParams],
  );

  return (
    <RocheUISearch<SearchCategory, RocheSearchResultItem>
      variant='main'
      itemRenderer={itemRenderer}
      showInput={true}
      support={support}
      searchState={searchState}
      searchResults={searchResults}
      isLoading={isLoading}
      hasQueriesBeenFetched={hasQueriesBeenFetched}
      resultsRef={resultsRef}
      onSearchParamsChange={handleSearchParamsChange}
      labels={labels}
      ariaLabels={ariaLabels}
    />
  );
};
